import React from 'react';
import { Link } from 'react-router-dom';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';

/**
 * Timeline activity item
 */
export interface TimelineActivity {
  id: number | string;
  campaign_id?: number;
  campaign_name?: string;
  decision?: 'bot' | 'human';
  ip_address?: string;
  country?: string;
  user_agent?: string;
  created_at: string | Date;
  type?: 'detection' | 'campaign_created' | 'campaign_updated' | 'custom';
  title?: string;
  description?: string;
}

/**
 * Props for ActivityTimeline component
 */
export interface ActivityTimelineProps {
  /**
   * Array of activities to display
   */
  activities: TimelineActivity[];

  /**
   * Maximum items to show
   */
  maxItems?: number;

  /**
   * Show "View All" link
   */
  showViewAll?: boolean;

  /**
   * Callback for "View All" click
   */
  onViewAll?: () => void;

  /**
   * Loading state
   */
  isLoading?: boolean;

  /**
   * Empty state message
   */
  emptyMessage?: string;

  /**
   * Custom className
   */
  className?: string;
}

/**
 * Group activities by date
 */
const groupActivitiesByDate = (activities: TimelineActivity[]) => {
  const groups: { [key: string]: TimelineActivity[] } = {};

  activities.forEach((activity) => {
    const date = new Date(activity.created_at);
    let groupKey: string;

    if (isToday(date)) {
      groupKey = 'Today';
    } else if (isYesterday(date)) {
      groupKey = 'Yesterday';
    } else {
      groupKey = format(date, 'MMMM d, yyyy');
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(activity);
  });

  return groups;
};

/**
 * Get icon for activity type
 */
const getActivityIcon = (activity: TimelineActivity): React.ReactNode => {
  if (activity.decision === 'bot') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  if (activity.decision === 'human') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (activity.type === 'campaign_created') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

/**
 * Get color for activity
 */
const getActivityColor = (activity: TimelineActivity) => {
  if (activity.decision === 'bot') {
    return {
      dot: 'bg-red-500',
      iconBg: 'bg-red-100',
      iconText: 'text-red-600',
      badge: 'bg-red-100 text-red-700',
    };
  }
  if (activity.decision === 'human') {
    return {
      dot: 'bg-green-500',
      iconBg: 'bg-green-100',
      iconText: 'text-green-600',
      badge: 'bg-green-100 text-green-700',
    };
  }
  return {
    dot: 'bg-blue-500',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
  };
};

/**
 * ActivityTimeline Component
 *
 * Displays recent activities in a visual timeline format with date grouping.
 * Improved over the basic activity list with better visual hierarchy.
 *
 * @example
 * ```tsx
 * <ActivityTimeline
 *   activities={recentActivity}
 *   maxItems={10}
 *   showViewAll
 *   onViewAll={() => navigate('/activity')}
 * />
 * ```
 */
export function ActivityTimeline({
  activities,
  maxItems = 10,
  showViewAll = false,
  onViewAll,
  isLoading = false,
  emptyMessage = 'No recent activity',
  className = '',
}: ActivityTimelineProps) {
  const displayedActivities = activities.slice(0, maxItems);
  const groupedActivities = groupActivitiesByDate(displayedActivities);

  if (isLoading) {
    return (
      <div
        className={`bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm ${className}`}
        data-testid="activity-timeline-loading"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></span>
          Recent Activity
        </h2>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4 animate-pulse">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                {i < 3 && <div className="w-0.5 h-12 bg-gray-200 mt-2"></div>}
              </div>
              <div className="flex-1 pb-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div
        className={`bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm ${className}`}
        data-testid="activity-timeline-empty"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></span>
          Recent Activity
        </h2>
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 text-sm">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm hover:shadow-md transition-shadow duration-300 ${className}`}
      data-testid="activity-timeline"
    >
      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <span className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></span>
        Recent Activity
      </h2>

      <div className="space-y-6">
        {Object.entries(groupedActivities).map(([dateGroup, groupActivities], groupIndex) => (
          <div key={dateGroup}>
            {/* Date Group Header */}
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide">{dateGroup}</h3>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Activities */}
            <div className="space-y-4">
              {groupActivities.map((activity, index) => {
                const colors = getActivityColor(activity);
                const isLast = index === groupActivities.length - 1 && groupIndex === Object.keys(groupedActivities).length - 1;

                return (
                  <div key={activity.id} className="flex gap-4 relative" data-testid="timeline-activity">
                    {/* Timeline */}
                    <div className="flex flex-col items-center">
                      {/* Icon Circle */}
                      <div className={`relative z-10 w-10 h-10 rounded-full ${colors.iconBg} ${colors.iconText} flex items-center justify-center shadow-sm`}>
                        {getActivityIcon(activity)}
                      </div>
                      {/* Connecting Line */}
                      {!isLast && (
                        <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {activity.campaign_name && (
                            <Link
                              to={`/campaigns/${activity.campaign_id}`}
                              className="text-sm font-semibold text-gray-900 hover:text-violet-600 transition-colors"
                            >
                              {activity.campaign_name}
                            </Link>
                          )}
                          {activity.title && (
                            <p className="text-sm font-semibold text-gray-900">{activity.title}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                            {activity.ip_address && (
                              <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                                {activity.ip_address}
                              </span>
                            )}
                            {activity.country && (
                              <span>{activity.country}</span>
                            )}
                            <span className="text-gray-400">•</span>
                            <span>{formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}</span>
                          </div>
                          {activity.description && (
                            <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                          )}
                        </div>

                        {/* Decision Badge */}
                        {activity.decision && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${colors.badge} shrink-0`}>
                            {activity.decision === 'bot' ? 'Bot' : 'Human'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* View All Link */}
      {showViewAll && activities.length > maxItems && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onViewAll}
            className="w-full py-2.5 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            data-testid="timeline-view-all"
          >
            View all activity
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default ActivityTimeline;
